import type { LumpsumRebalanceCategory } from '../../../api/rebalance.types'
import { PlansActionFilter, PlansPlanMode } from '../../../api/types'
import {
  isListPlansFetchFailed,
  isListPlansLoading,
  useLumpsumPlansContext
} from '../../LumpsumPlansProvider'
import { PlansListEmptyState } from '../../lumpsum-ui-components/PlansListEmptyState'
import { PlansListFilterEmptyState } from '../../lumpsum-ui-components/PlansListFilterEmptyState'
import { PlansCategoryListSkeleton } from '../../lumpsum-ui-components/PlansCategoryListSkeleton'
import { usePlanCategoryList } from '../../usePlanCategoryBucketExpansion'
import { RebalanceCategoryCard } from './RebalanceCategoryCard'
import { RebalancePlansListErrorState } from './RebalancePlansListErrorState'
import { isPlansListFullyEmpty, resolvePlanCategoryKey } from '../../../../utils/lumpsum/planListUtils'

type RebalanceCategoryListProps = {
  /** When set, render these instead of context and skip query loading/error/empty shells. */
  categories?: LumpsumRebalanceCategory[] | null
}

export function RebalanceCategoryList({
  categories: dummyCategories
}: RebalanceCategoryListProps = {}) {
  const { actionFiltersByPlanMode, rebalancePlansData, rebalancePlansQuery } =
    useLumpsumPlansContext()
  const hasDummyCategories = dummyCategories !== undefined
  const rebalanceCategories = hasDummyCategories
    ? (dummyCategories ?? [])
    : rebalancePlansData?.categories
  const actionFilter = hasDummyCategories
    ? PlansActionFilter.ALL
    : (actionFiltersByPlanMode[PlansPlanMode.REBALANCE] ?? PlansActionFilter.ALL)

  const categoryEntries = usePlanCategoryList<LumpsumRebalanceCategory>(
    rebalanceCategories,
    actionFilter,
    PlansPlanMode.REBALANCE
  )

  if (!hasDummyCategories) {
    if (isListPlansLoading(PlansPlanMode.REBALANCE, rebalancePlansQuery)) {
      return <PlansCategoryListSkeleton />
    }

    if (isListPlansFetchFailed(PlansPlanMode.REBALANCE, rebalancePlansQuery)) {
      return <RebalancePlansListErrorState onRetry={rebalancePlansQuery.refetch} />
    }

    if (isPlansListFullyEmpty(rebalanceCategories)) {
      return (
        <PlansListEmptyState
          planMode={PlansPlanMode.REBALANCE}
          planSnapshotDate={rebalancePlansData?.planSnapshotDate}
          planDurationInMonths={rebalancePlansData?.planDurationInMonths}
        />
      )
    }

    if (categoryEntries.length <= 0) {
      return <PlansListFilterEmptyState actionFilter={actionFilter} />
    }
  }

  return (
    <>
      <RebalanceCategoryListStickyBar categoryCount={categoryEntries.length} />
      {categoryEntries.map(({ category, categoryIndex }, entryIndex) => (
        <RebalanceCategoryCard
          key={resolvePlanCategoryKey(category, categoryIndex)}
          category={category}
          categoryIndex={categoryIndex}
          actionFilter={actionFilter}
          noDivider={entryIndex === 0}
        />
      ))}
    </>
  )
}

export function RebalanceCategoryListStickyBar({ categoryCount }: { categoryCount: number }) {
  return (
    <div className="sticky top-[-2px] z-10 flex items-center justify-between bg-white px-5 py-4 border-b border-[#E8EBEA]">
      <div className="text-[13px] font-[500] leading-[150%] text-[#232323]">
        {categoryCount} {categoryCount === 1 ? 'Category' : 'Categories'}
      </div>
      <div className="text-[13px] font-[500] leading-[150%] text-[#232323]">Actions</div>
    </div>
  )
}
